import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { Slide } from "@/lib/carouselData";

interface CarouselProps {
  slides: Slide[];
  accentColor: string; // tailwind bg class for active dot
}

const SLIDE_VARIANTS = {
  enter: (dir: number) => ({ x: dir > 0 ? "100%" : "-100%", opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir > 0 ? "-100%" : "100%", opacity: 0 }),
};

export function InstagramCarousel({ slides, accentColor }: CarouselProps) {
  const [[page, direction], setPage] = useState([0, 0]);

  const paginate = useCallback(
    (newDir: number) => {
      const next = page + newDir;
      if (next < 0 || next >= slides.length) return;
      setPage([next, newDir]);
    },
    [page, slides.length]
  );

  const goTo = useCallback(
    (idx: number) => {
      setPage([idx, idx > page ? 1 : -1]);
    },
    [page]
  );

  const slide = slides[page];
  const isCover = slide.step === 0;
  const isLast = page === slides.length - 1;

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Square Instagram frame */}
      <div
        className="relative overflow-hidden rounded-2xl shadow-2xl select-none"
        style={{ width: "min(540px, 90vw)", height: "min(540px, 90vw)" }}
      >
        {/* Background image layer */}
        <div
          className="absolute inset-0 bg-cover bg-center transition-all duration-700"
          style={{ backgroundImage: `url(${slide.bg})` }}
        />
        {/* Dark overlay */}
        <div
          className={`absolute inset-0 ${
            isCover
              ? "bg-gradient-to-b from-black/60 via-black/50 to-black/75"
              : "bg-gradient-to-b from-black/55 via-black/40 to-black/70"
          }`}
        />

        {/* Animated slide content */}
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <motion.div
            key={page}
            custom={direction}
            variants={SLIDE_VARIANTS}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: "spring", stiffness: 320, damping: 36 }}
            className="absolute inset-0 flex flex-col"
          >
            {/* Top bar — agent branding */}
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-lg"
                  style={{ background: "oklch(0.68 0.12 45)", color: "oklch(0.15 0.02 240)" }}
                >
                  SK
                </div>
                <span className="text-white/90 text-xs font-semibold tracking-widest uppercase">
                  Saskatoon Real Estate
                </span>
              </div>
              <span className="text-white/60 text-xs font-mono">
                {page + 1} / {slides.length}
              </span>
            </div>

            {/* Main content */}
            <div className="flex-1 flex flex-col justify-center px-6 py-4">
              {isCover ? (
                /* Cover slide layout */
                <div className="flex flex-col items-center justify-center text-center gap-5 h-full">
                  <motion.div
                    initial={{ scale: 0.7, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.15, type: "spring", stiffness: 300, damping: 22 }}
                    className="text-6xl"
                  >
                    {slide.emoji}
                  </motion.div>
                  <motion.h1
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className="text-white font-bold tracking-tight leading-none"
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontSize: "clamp(2rem, 7vw, 3rem)",
                    }}
                  >
                    {slide.title}
                  </motion.h1>
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.35, duration: 0.5 }}
                    className="h-0.5 w-20 rounded-full"
                    style={{ background: "oklch(0.68 0.12 45)" }}
                  />
                  <motion.p
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4, duration: 0.5 }}
                    className="text-white/80 text-sm font-medium tracking-widest uppercase"
                  >
                    {slide.subtitle}
                  </motion.p>
                  <motion.p
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                    className="text-white/70 text-sm max-w-[320px] leading-relaxed"
                  >
                    {slide.bullets[0]}
                  </motion.p>
                  <motion.div
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="mt-2 px-5 py-2 rounded-full text-xs font-bold tracking-widest uppercase"
                    style={{
                      background: "oklch(0.68 0.12 45)",
                      color: "oklch(0.15 0.02 240)",
                    }}
                  >
                    Swipe to Begin →
                  </motion.div>
                </div>
              ) : isLast ? (
                /* Celebration / possession day slide */
                <div className="flex flex-col items-center justify-center text-center gap-4 h-full">
                  <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: [1.2, 1], opacity: 1 }}
                    transition={{ delay: 0.1, type: "spring", stiffness: 260, damping: 18 }}
                    className="text-6xl"
                  >
                    {slide.emoji}
                  </motion.div>
                  <motion.h2
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="text-white font-bold leading-none"
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontSize: "clamp(1.8rem, 7vw, 2.8rem)",
                    }}
                  >
                    {slide.title}
                  </motion.h2>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="text-white/80 text-xs tracking-widest uppercase font-semibold"
                  >
                    {slide.subtitle}
                  </motion.p>
                  <div className="flex flex-col gap-2 mt-1 w-full max-w-xs">
                    {slide.bullets.map((b, i) => (
                      <motion.p
                        key={i}
                        initial={{ x: -15, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.35 + i * 0.1 }}
                        className="text-white/85 text-sm text-center"
                      >
                        {b}
                      </motion.p>
                    ))}
                  </div>
                  {slide.accent && (
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.7, type: "spring" }}
                      className="mt-3 px-6 py-2.5 rounded-full font-bold text-sm shadow-lg"
                      style={{
                        background: "oklch(0.68 0.12 45)",
                        color: "oklch(0.15 0.02 240)",
                      }}
                    >
                      {slide.accent}
                    </motion.div>
                  )}
                </div>
              ) : (
                /* Regular step slide */
                <div className="flex flex-col gap-3 h-full justify-center">
                  {/* Step badge */}
                  <motion.div
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.05 }}
                    className="flex items-center gap-2"
                  >
                    <span
                      className="text-xs font-bold px-2.5 py-1 rounded-full tracking-widest uppercase"
                      style={{
                        background: "oklch(0.68 0.12 45 / 0.9)",
                        color: "oklch(0.15 0.02 240)",
                      }}
                    >
                      Step {slide.step}
                    </span>
                  </motion.div>

                  {/* Emoji + Headline */}
                  <motion.div
                    initial={{ y: 15, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <span className="text-4xl leading-none">{slide.emoji}</span>
                    <h2
                      className="text-white font-bold leading-tight"
                      style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontSize: "clamp(1.4rem, 5vw, 2rem)",
                      }}
                    >
                      {slide.title}
                    </h2>
                  </motion.div>

                  {/* Subtitle */}
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.15 }}
                    className="text-white/65 text-xs font-semibold tracking-wider uppercase"
                  >
                    {slide.subtitle}
                  </motion.p>

                  {/* Divider */}
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.2, duration: 0.4 }}
                    className="h-px w-full origin-left"
                    style={{ background: "oklch(0.68 0.12 45 / 0.5)" }}
                  />

                  {/* Bullet points */}
                  <div className="flex flex-col gap-2">
                    {slide.bullets.map((bullet, i) => (
                      <motion.div
                        key={i}
                        initial={{ x: -15, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.25 + i * 0.08 }}
                        className="flex items-start gap-2"
                      >
                        <span
                          className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ background: "oklch(0.68 0.12 45)" }}
                        />
                        <p className="text-white/90 text-sm leading-snug">{bullet}</p>
                      </motion.div>
                    ))}
                  </div>

                  {/* Accent callout */}
                  {slide.accent && (
                    <motion.div
                      initial={{ y: 8, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.5 }}
                      className="mt-1 px-4 py-2 rounded-xl text-xs font-bold"
                      style={{
                        background: "oklch(0.28 0.08 240 / 0.75)",
                        border: "1px solid oklch(0.68 0.12 45 / 0.6)",
                        color: "oklch(0.68 0.12 45)",
                        fontFamily: "'JetBrains Mono', monospace",
                      }}
                    >
                      {slide.accent}
                    </motion.div>
                  )}
                </div>
              )}
            </div>

            {/* Bottom: progress dots */}
            <div className="flex items-center justify-center gap-1.5 pb-5">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`rounded-full transition-all duration-300 cursor-pointer ${
                    i === page
                      ? "w-6 h-2"
                      : "w-2 h-2 bg-white/35 hover:bg-white/60"
                  }`}
                  style={i === page ? { background: "oklch(0.68 0.12 45)" } : {}}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation arrows */}
        {page > 0 && (
          <button
            onClick={() => paginate(-1)}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center bg-black/40 hover:bg-black/60 text-white transition-all z-10 backdrop-blur-sm"
            aria-label="Previous slide"
          >
            <ChevronLeft size={18} />
          </button>
        )}
        {page < slides.length - 1 && (
          <button
            onClick={() => paginate(1)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center bg-black/40 hover:bg-black/60 text-white transition-all z-10 backdrop-blur-sm"
            aria-label="Next slide"
          >
            <ChevronRight size={18} />
          </button>
        )}
      </div>

      {/* Slide title below carousel */}
      <p className="text-muted-foreground text-xs text-center tracking-wide">
        {slide.step === 0 ? "Cover Slide" : isLast ? "Final Slide" : `Step ${slide.step} of ${slides.length - 2}`} — {slide.title}
      </p>
    </div>
  );
}
